import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Autocomplete,
  TextField,
  Divider,
  LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import type { InventoryItem } from '../inventory/types/inventory.types';
import { InventoryService } from '../inventory/services';
import GuideButton from '../../common/guide/GuideButton';

interface ParsedInvoiceItem {
  sequenceNumber?: number; // ✅ PDF'deki "Sıra No" sütunundaki değer
  itemName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate: number;
  discountRate?: number;
  lineNumber: number; // ✅ Internal parsing referansı
}

interface MatchedInvoiceItem {
  parsedItem: ParsedInvoiceItem;
  matchedItemId: string | null;
  matchedItemName: string | null;
  matchConfidence: number;
  isManualItem: boolean;
  matchDescription: string;
}

interface PdfParsingResult {
  success: boolean;
  message: string;
  items: MatchedInvoiceItem[];
  totalItemsFound: number;
  autoMatchedCount: number;
  manualMatchRequired: number;
  processingTimeMs: number;
  warnings: string[];
}

interface PdfInvoiceUploadModalProps {
  open: boolean;
  onClose: () => void;
  onItemsLoaded: (items: any[]) => void;
  companyId: string;
}

const PdfInvoiceUploadModal: React.FC<PdfInvoiceUploadModalProps> = ({
  open,
  onClose,
  onItemsLoaded,
  companyId
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<PdfParsingResult | null>(null);
  const [manualMatches, setManualMatches] = useState<{ [key: number]: string | null }>({});
  
  // 🔍 Server-side arama için state'ler
  const [searchResults, setSearchResults] = useState<{ [key: number]: InventoryItem[] }>({});
  const [searchLoading, setSearchLoading] = useState<{ [key: number]: boolean }>({});
  const [searchInputs, setSearchInputs] = useState<{ [key: number]: string }>({});

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.pdf')) {
        setError(tt('Sadece PDF dosyaları yüklenebilir', 'Only PDF files can be uploaded'));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(tt('Dosya boyutu 10MB\'dan küçük olmalıdır', 'File size must be less than 10MB'));
        return;
      }
      setSelectedFile(file);
      setError(null);
      setParseResult(null);
    }
  };

  const handleUploadAndParse = async () => {
    if (!selectedFile) {
      setError(tt('Lütfen bir PDF dosyası seçin', 'Please select a PDF file'));
      return;
    }

    setUploading(true);
    setParsing(true);
    setError(null);

    try {
      console.log('📄 [PDF UPLOAD] PDF yükleniyor ve parse ediliyor...');

      const formData = new FormData();
      formData.append('pdfFile', selectedFile);
      formData.append('minMatchConfidence', '60');
      formData.append('enableAutoMatching', 'true');

      const response = await accountingApi.post(ACCOUNTING_API.INVOICE_PARSE_PDF, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const result = response.data;

      if (!result.success) {
        throw new Error(result.message || tt('PDF işlenemedi', 'PDF could not be processed'));
      }

      console.log('✅ [PDF UPLOAD] Parse başarılı:', result.data);
      setParseResult(result.data);

    } catch (err: any) {
      console.error('❌ [PDF UPLOAD] Hata:', err);
      setError(err.message || tt('PDF yüklenirken bir hata oluştu', 'An error occurred while uploading PDF'));
    } finally {
      setUploading(false);
      setParsing(false);
    }
  };

  const handleManualMatch = (itemIndex: number, selectedItemId: string | null) => {
    setManualMatches(prev => ({
      ...prev,
      [itemIndex]: selectedItemId
    }));
  };

  // 🔍 Server-side arama fonksiyonu - ✅ Inventory.tsx ile aynı mantık kullanıyoruz!
  const handleSearchInventory = async (itemIndex: number, searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults(prev => ({ ...prev, [itemIndex]: [] }));
      return;
    }

    setSearchLoading(prev => ({ ...prev, [itemIndex]: true }));

    try {
      console.log(`🔍 [PDF MODAL] Arama yapılıyor: "${searchTerm}"`);
      
      // ✅ FIX: POST request kullan (Inventory.tsx ile aynı) - Doğru metod adı: getPagedItemsSQL
      const result = await InventoryService.getPagedItemsSQL(
        companyId,
        1, // pageNumber
        50, // pageSize
        searchTerm // searchTerm - backend'de LIKE '%...%' ile aranacak
      );
      
      // ✅ DTO'yu InventoryItem tipine dönüştür
      const inventoryItems: InventoryItem[] = result.items.map(dto => ({
        id: dto.id,
        companyId: dto.companyId,
        name: dto.name,
        stockCode: dto.stockCode,
        barcode: dto.barcode || '',
        categoryId: dto.categoryId || null,
        brandId: dto.brandId || null,
        branchesId: dto.branchesId || null,
        unit: dto.unit,
        purchasePrice: dto.purchasePrice || 0,
        purchaseCurrency: dto.purchaseCurrency || 'TRY',
        salePrice: dto.salePrice,
        saleCurrency: dto.saleCurrency,
        listPrice: dto.listPrice,
        listCurrency: dto.listCurrency,
        description: dto.description || '',
        imageUrl: dto.imageUrl || null,
        vatRate: dto.vatRate,
        vatStatus: dto.vatStatus,
        sctRate: dto.sctRate,
        sctStatus: dto.sctStatus,
        isActive: dto.isActive,
        isEcommerce: dto.isEcommerce || false,
        createdAt: dto.createdAt,
        updatedAt: dto.updatedAt || dto.createdAt,
        warehouseStocks: dto.warehouseStocks || [],
      }));
      
      console.log(`✅ [PDF MODAL] "${searchTerm}" için ${inventoryItems.length} ürün bulundu (Toplam: ${result.totalCount})`);
      setSearchResults(prev => ({ ...prev, [itemIndex]: inventoryItems }));
    } catch (err) {
      console.error('❌ [PDF MODAL] Arama hatası:', err);
      setSearchResults(prev => ({ ...prev, [itemIndex]: [] }));
    } finally {
      setSearchLoading(prev => ({ ...prev, [itemIndex]: false }));
    }
  };

  const handleImportItems = () => {
    if (!parseResult) return;

    const itemsToImport = parseResult.items.map((matchedItem, index) => {
      let itemId = matchedItem.matchedItemId;
      let itemName = matchedItem.parsedItem.itemName;
      let isManual = matchedItem.isManualItem;

      // Manuel eşleştirme varsa kullan
      if (manualMatches[index]) {
        itemId = manualMatches[index];
        // Arama sonuçlarından eşleşen ürünü bul
        const manuallyMatchedItem = searchResults[index]?.find(inv => inv.id === itemId);
        if (manuallyMatchedItem) {
          itemName = manuallyMatchedItem.name;
          isManual = false;
        }
      }

      return {
        id: Date.now().toString() + '_' + index,
        itemId: isManual ? 'manual_' + Date.now() + '_' + index : itemId || '',
        itemName: itemName,
        quantity: matchedItem.parsedItem.quantity,
        unit: matchedItem.parsedItem.unit,
        unitPrice: matchedItem.parsedItem.unitPrice,
        vatRate: matchedItem.parsedItem.vatRate,
        sctRate: 0,
        discountRate: matchedItem.parsedItem.discountRate || 0,
        discountAmount: 0,
        lineTotal: matchedItem.parsedItem.quantity * matchedItem.parsedItem.unitPrice,
        netTotal: 0,
        vatAmount: 0,
        sctAmount: 0,
        grossTotal: 0,
        displayQuantity: matchedItem.parsedItem.quantity.toString().replace('.', ',')
      };
    });

    console.log('✅ [PDF UPLOAD] Ürünler aktarılıyor:', itemsToImport);
    onItemsLoaded(itemsToImport);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setParseResult(null);
    setError(null);
    setManualMatches({});
    onClose();
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'success';
    if (confidence >= 60) return 'warning';
    return 'error';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 90) return tt('Yüksek', 'High');
    if (confidence >= 60) return tt('Orta', 'Medium');
    return tt('Düşük', 'Low');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PictureAsPdfIcon color="error" />
            <Typography variant="h6">
              {tt('PDF Faturadan Ürün Aktar', 'Import Items from PDF Invoice')}
            </Typography>
            <GuideButton guideKey="documents.pdfInvoiceImport" size="small" />
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* STEP 1: File Selection */}
        {!parseResult && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {tt('1. PDF Dosyası Seçin', '1. Select PDF File')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                {tt('Dosya Seç', 'Select File')}
                <input
                  type="file"
                  hidden
                  accept=".pdf"
                  onChange={handleFileSelect}
                />
              </Button>
              {selectedFile && (
                <Chip
                  icon={<PictureAsPdfIcon />}
                  label={selectedFile.name}
                  onDelete={() => setSelectedFile(null)}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            {selectedFile && !parsing && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUploadAndParse}
                  disabled={uploading}
                  fullWidth
                  size="large"
                >
                  {uploading ? tt('İşleniyor...', 'Processing...') : tt('PDF\'i Analiz Et', 'Analyze PDF')}
                </Button>
              </Box>
            )}

            {parsing && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {tt('PDF analiz ediliyor, lütfen bekleyin...', 'Analyzing PDF, please wait...')}
                </Typography>
                <LinearProgress />
              </Box>
            )}
          </Box>
        )}

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* STEP 2: Parse Results */}
        {parseResult && (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {tt('2. Eşleşme Sonuçları', '2. Matching Results')}
            </Typography>

            {/* Özet Bilgi */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${tt('Toplam', 'Total')}: ${parseResult.totalItemsFound} ${tt('ürün', 'items')}`}
                color="primary"
              />
              <Chip
                icon={<CheckCircleIcon />}
                label={`${tt('Otomatik', 'Auto')}: ${parseResult.autoMatchedCount}`}
                color="success"
              />
              {parseResult.manualMatchRequired > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${tt('Manuel', 'Manual')}: ${parseResult.manualMatchRequired}`}
                  color="warning"
                />
              )}
              <Chip
                label={`${tt('Süre', 'Duration')}: ${parseResult.processingTimeMs}ms`}
                variant="outlined"
              />
            </Box>

            {/* Uyarılar */}
            {parseResult.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {parseResult.warnings.map((warning, i) => (
                  <div key={i}>{warning}</div>
                ))}
              </Alert>
            )}

            {/* Eşleşen Ürünler Tablosu */}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ width: 60, fontWeight: 'bold' }}>{tt('Sıra', 'No')}</TableCell>
                    <TableCell>{tt("PDF'deki Ürün", 'Item in PDF')}</TableCell>
                    <TableCell>{tt('Stok Eşleşmesi', 'Stock Match')}</TableCell>
                    <TableCell align="center">{tt('Güven', 'Confidence')}</TableCell>
                    <TableCell align="right">{tt('Miktar', 'Quantity')}</TableCell>
                    <TableCell align="right">{tt('Birim Fiyat', 'Unit Price')}</TableCell>
                    <TableCell align="right">KDV %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* ✨ Eşleşmeyen ürünleri en üste taşı */}
                  {parseResult.items
                    .sort((a, b) => {
                      // Manuel eşleştirme gerekenleri (isManualItem: true) en üste
                      if (a.isManualItem && !b.isManualItem) return -1;
                      if (!a.isManualItem && b.isManualItem) return 1;
                      // Aynı tür ise orijinal sırayı koru (lineNumber'a göre)
                      return a.parsedItem.lineNumber - b.parsedItem.lineNumber;
                    })
                    .map((item, sortedIndex) => {
                      // Orijinal index'i bulmak için parsedItem.lineNumber - 1 kullan
                      const originalIndex = parseResult.items.findIndex(
                        i => i.parsedItem.lineNumber === item.parsedItem.lineNumber
                      );
                      return (
                    <TableRow 
                      key={sortedIndex}
                      sx={{
                        // Eşleşmeyen ürünleri vurgula
                        backgroundColor: item.isManualItem ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                        borderLeft: item.isManualItem ? '4px solid' : 'none',
                        borderLeftColor: item.isManualItem ? 'warning.main' : 'transparent',
                        '&:hover': {
                          backgroundColor: item.isManualItem ? 'rgba(255, 152, 0, 0.15)' : 'action.hover'
                        }
                      }}
                    >
                      {/* ✅ YENİ: Sıra Numarası Sütunu (PDF'deki "Sıra No" sütunu) */}
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: '1rem',
                          color: item.isManualItem ? 'warning.main' : 'text.primary',
                          backgroundColor: item.isManualItem ? 'rgba(255, 152, 0, 0.15)' : 'transparent'
                        }}
                      >
                        {item.parsedItem.sequenceNumber ?? item.parsedItem.lineNumber}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {item.parsedItem.itemName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.isManualItem ? (
                          <Autocomplete
                            size="small"
                            options={searchResults[originalIndex] || []}
                            getOptionLabel={(option) => option.name}
                            value={(searchResults[originalIndex] || []).find(inv => inv.id === manualMatches[originalIndex]) || null}
                            onChange={(_, newValue) => handleManualMatch(originalIndex, newValue?.id || null)}
                            onInputChange={(_, newInputValue) => {
                              setSearchInputs(prev => ({ ...prev, [originalIndex]: newInputValue }));
                              handleSearchInventory(originalIndex, newInputValue);
                            }}
                            loading={searchLoading[originalIndex] || false}
                            filterOptions={(x) => x}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box>
                                  <Typography variant="body2">{option.name}</Typography>
                                  {(option.stockCode || option.barcode) && (
                                    <Typography variant="caption" color="text.secondary">
                                      {option.stockCode && `Kod: ${option.stockCode}`}
                                      {option.stockCode && option.barcode && ' | '}
                                      {option.barcode && `Barkod: ${option.barcode}`}
                                    </Typography>
                                  )}
                                </Box>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={tt('Stokta Ara', 'Search in Stock')}
                                placeholder={tt('Ürün adı, kodu veya barkod ile ara (min 2 karakter)', 'Search by item name, code or barcode (min 2 chars)')}
                                helperText={
                                  searchLoading[originalIndex]
                                    ? tt('Aranıyor...', 'Searching...')
                                    : searchInputs[originalIndex] && searchInputs[originalIndex].length >= 2
                                    ? `${(searchResults[originalIndex] || []).length} ${tt('sonuç bulundu', 'results found')}`
                                    : tt('Aramaya başlamak için en az 2 karakter girin', 'Enter at least 2 characters to start searching')
                                }
                              />
                            )}
                            sx={{ minWidth: 300 }}
                            noOptionsText={tt('Stokta ürün bulunamadı', 'No stock item found')}
                            loadingText={tt('Yükleniyor...', 'Loading...')}
                          />
                        ) : (
                          <Box>
                            <Typography variant="body2" color="success.main">
                              ✓ {item.matchedItemName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.matchDescription}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${item.matchConfidence}% - ${getConfidenceLabel(item.matchConfidence)}`}
                          color={getConfidenceColor(item.matchConfidence)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {item.parsedItem.quantity} {item.parsedItem.unit}
                      </TableCell>
                      <TableCell align="right">
                        {item.parsedItem.unitPrice.toFixed(2)} ₺
                      </TableCell>
                      <TableCell align="right">
                        %{item.parsedItem.vatRate}
                      </TableCell>
                    </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            {parseResult.manualMatchRequired > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>⚠️ {tt(`Manuel eşleştirme gereken ${parseResult.manualMatchRequired} ürün en üstte gösteriliyor.`, `${parseResult.manualMatchRequired} items requiring manual matching are shown at the top.`)}</strong>
                <br />
                {tt('Açılır listeden stok ürünü seçin veya boş bırakarak stoksuz ürün olarak ekleyin.', 'Select a stock item from dropdown or leave empty to add as non-stock item.')}
              </Alert>
            )}
            {parseResult.manualMatchRequired === 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <strong>✅ {tt('Tüm ürünler otomatik olarak eşleştirildi!', 'All items are matched automatically!')}</strong>
                <br />
                {tt('Kontrol edip faturaya aktarabilirsiniz.', 'You can review and import into invoice.')}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined">
          {tt('İptal', 'Cancel')}
        </Button>
        {parseResult && (
          <Button
            onClick={handleImportItems}
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            size="large"
          >
            {`${parseResult.items.length} ${tt('Ürünü Faturaya Aktar', 'Items to Import into Invoice')}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PdfInvoiceUploadModal;

